import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Icon, Popup } from "semantic-ui-react";

import { AttachmentsPreview, DATE_FORMATS_FNS } from "components";
import { CommunicationRemarkActionSelector } from "modules/AppointmentDetails/components/CustomerActions/components";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment, CheckInRemark, DiagnoseOverviewRemark, KeyLockerRemark, KioskRemark } from "models";
import type { RemarkIdProperty } from "modules/AppointmentDetails/components/CustomerActions/components";
import type { InclusiveUnion } from "utils/type";

type CommunicationRemarksProps<T> = {
  title: string;
  remarks?: T[];
  remarkIdProperty: RemarkIdProperty;
  color?: string;
  icon?: string;
  canCreateIntervention: boolean;
  canToggleHandled: boolean;
  appointment: Appointment;
  onToggleHandled: (remark: T) => void;
  onInterventionConverted: (mark: T) => void;
};

export const CommunicationRemarks = <T extends InclusiveUnion<CheckInRemark | DiagnoseOverviewRemark | KeyLockerRemark | KioskRemark>>({
  title,
  remarks,
  remarkIdProperty,
  color = "blue",
  icon = "price tag",
  canCreateIntervention = true,
  canToggleHandled,
  onToggleHandled,
  appointment,
  onInterventionConverted
}: CommunicationRemarksProps<T>) => {
  const [visibleCustomerActionAttachmentId, setVisibleCustomerActionAttachmentId] = useState<number | null>();
  const { t } = useTranslation();

  if (!remarks?.length) return null;

  const showMedia = (id: number) => setVisibleCustomerActionAttachmentId(current => (current === id ? null : id));

  return (
    <div className="CustomerActions-items">
      {remarks
        .sort((a, b) => a.id - b.id)
        .map(remark => {
          return (
            <Grid key={remark.id} className="actions-container">
              <Grid.Column width={13}>
                <div className={`icon-container ${color}`}>
                  <Popup
                    wide
                    basic
                    content={
                      <div className="InfoPopup">
                        <div className="InfoTitle">{title}</div>
                        {remark.customer_name && (
                          <div className="InfoDescription">
                            {t("v8_made_by", "Made by")} {remark.customer_name}
                          </div>
                        )}
                        <div className="InfoContent">
                          <span>{t("v8_on", "On")}</span> {`${formatDate(remark.created_on, DATE_FORMATS_FNS.dateMonthYearTime)}`}
                        </div>
                      </div>
                    }
                    trigger={<Icon className={icon} />}
                  />
                </div>
                {remark.title && <p className="title">{remark.title}</p>}
                {remark.description && <p className="description">{remark.description}</p>}
              </Grid.Column>
              <Grid.Column width={3}>
                <div className="end-grid">
                  {remark.attachments?.length && (
                    <div onClick={() => showMedia(remark.id)} className="image-action">
                      <Icon className="images grey pointer" size="small" />
                      <p>
                        <small>{remark.attachments.length}</small>
                      </p>
                    </div>
                  )}
                  <div className="status-container">
                    <div className={`changeHandleStatus ${!canToggleHandled ? "disabled" : ""}`} onClick={canToggleHandled ? () => onToggleHandled(remark) : undefined}>
                      <Icon className={remark.receptionist_handled ? "check circle outline green" : "circle xmark outline red"} />
                    </div>

                    {canCreateIntervention && (
                      <CommunicationRemarkActionSelector
                        remark={remark}
                        remarkIdProperty={remarkIdProperty}
                        remarkId={remark.id}
                        appointment={appointment}
                        onInterventionConverted={() => onInterventionConverted(remark)}
                      />
                    )}
                  </div>
                </div>
              </Grid.Column>
              {visibleCustomerActionAttachmentId === remark.id && (
                <Grid.Column width={16}>
                  <AttachmentsPreview attachments={remark.attachments} />
                </Grid.Column>
              )}
            </Grid>
          );
        })}
    </div>
  );
};
